// Appointments › Rated Orders — low-rating queue for support (RATED_ORDERS_PLAN.md §5.2).
// Same shell as Appointments / Requests: ActiveHelper → ListPageHeading (global search,
// filters, export) + the shared MainPage table (column types, action buttons, row click).
// Data: live listener on bookings where complaintOpen == true (the queue) merged with a
// one-shot lowRated == true query for the selected date range (resolved history).
// Reviews are NOT joined: the booking carries rate + the three sub-rates + reason.
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import Select from 'react-select';
import { Badge } from 'reactstrap';
import ActiveHelper from '../../../components/common/activeHelper';
import { Colxx } from '../../../components/common/CustomBootstrap';
import Rating from '../../../components/common/Rating';
import { ORDER_COLLECTION } from '../../../config/env';
import { firestore } from '../../../helpers/Firebase';
import { accessTypes, createNotification, deviceWidth } from '../../../helpers/Utils';
import '../CRM/crmstyles.css';
import CaseModal from './components/CaseModal';
import Order from './components/order';
import {
  COMPLAINT_STATUS, COMPLAINT_STATUS_ORDER, PILL,
  canManageComplaints, canViewRatedOrders, complaintAgeHours, isProviderUser,
} from './components/complaint';

const statusOptions = COMPLAINT_STATUS_ORDER.map((value) => ({ value, label: COMPLAINT_STATUS[value].label, key: value }));
const csvCell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
const phoneOf = (b) => `${b?.country_code || ''}${b?.customer_number || ''}`.replace(/\D/g, '');
const vehicleOf = (b) => (Array.isArray(b?.carDetail) ? b.carDetail : [])
  .map((c) => [c?.car_model || c?.brand || c?.make, c?.modelNumber?.model || c?.model].filter(Boolean).join(' '))
  .filter(Boolean).join(', ');
const scoreOf = (b) => {
  const n = Number(b?.rate);
  if (Number.isFinite(n)) return n;
  const parts = [b?.serviceRate, b?.workerRate, b?.arrivalTimingRate].map(Number).filter(Number.isFinite);
  return parts.length ? Math.round((parts.reduce((a, c) => a + c, 0) / parts.length) * 10) / 10 : null;
};
const median = (arr) => {
  if (!arr.length) return null;
  const s = [...arr].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};
const hours = (ms) => (ms == null ? '—' : ms < 3600e3 ? `${Math.round(ms / 60e3)} min` : ms < 48 * 3600e3 ? `${(ms / 3600e3).toFixed(1)} h` : `${(ms / 86400e3).toFixed(1)} d`);
// Filter option: name + muted "N open" (selected chips show the name only).
const countLabel = (o, { context }) => (context === 'value' ? o.name : (
  <span className="d-flex justify-content-between" style={{ gap: '12px' }}>
    <span>{o.name}</span>
    <span className="text-muted text-small text-nowrap">{o.open} open{o.total > o.open ? ` · ${o.total} total` : ''}</span>
  </span>
));
// Row shape the shared table works with: flat accessors + the raw booking fields.
const toRow = (d) => {
  const b = d.data() || {};
  return { ...b, key: d.id, bookingId: d.id, caseStatus: b.complaint?.status || 'pending', score: scoreOf(b), vehicle: vehicleOf(b), phone: phoneOf(b) };
};

// Rendered through <Route component>, so react-router v4 passes `location` as a prop (no hooks in v4).
const RatedOrders = ({ location }) => {
  const { user } = useSelector((state) => state.authUser);
  const { provider, washers, bookings, services } = useSelector((state) => state.providers);
  const search = useSelector((state) => state.settings.search) || '';

  const [dateRange, setDateRange] = useState([moment().subtract(30, 'days').startOf('day').toDate(), new Date()]);
  const [openRows, setOpenRows] = useState(null);     // live queue, keyed by id (null = loading)
  const [historyRows, setHistoryRows] = useState({}); // one-shot, keyed by id
  const [filters, setFilters] = useState({ statuses: [], providers: [], services: [] });
  const [selectedBooking, setSelectedBooking] = useState(false);
  const [caseFor, setCaseFor] = useState(null);           // row open in the case modal
  const [slaHours, setSlaHours] = useState(24);
  const deepLinkHandled = useRef(false);
  // Providers get the same page read-only, limited to bookings assigned to them.
  const isProvider = isProviderUser(user);
  const canManage = canManageComplaints(user);
  const spScope = useCallback((ref) => (isProvider ? ref.where('sp_id', '==', user?.uid) : ref), [isProvider, user?.uid]);

  // Live queue. Equality-only on purpose: no composite index, and the open queue is small enough to sort here.
  useEffect(() => {
    if (!canViewRatedOrders(user)) return undefined;
    const unsub = spScope(firestore().collection(ORDER_COLLECTION).where('complaintOpen', '==', true))
      .onSnapshot((snap) => {
        const next = {};
        snap.forEach((d) => { next[d.id] = toRow(d); });
        setOpenRows(next);
      }, (e) => { console.error('rated orders queue', e); createNotification('error', e?.message || 'Queue listener failed', 'Rated Orders'); setOpenRows({}); });
    firestore().collection('settings').doc('ratedOrders').get()
      .then((s) => { const h = Number(s.data()?.pendingSlaHours); if (Number.isFinite(h) && h > 0) setSlaHours(h); }).catch(() => { });
    return () => unsub();
  }, [spScope]); // eslint-disable-line react-hooks/exhaustive-deps

  // History for the date range (open ones in range come back too; the merge dedupes)
  const loadHistory = useCallback(async (range) => {
    if (!range?.[0] || !range?.[1]) return;
    const from = moment(range[0]).startOf('day').valueOf();
    const to = moment(range[1]).endOf('day').valueOf();
    try {
      let docs;
      try {
        const snap = await spScope(firestore().collection(ORDER_COLLECTION).where('lowRated', '==', true))
          .where('rated', '>=', from).where('rated', '<=', to).orderBy('rated', 'desc').get();
        docs = snap.docs;
      } catch (e) {
        if (!/index/i.test(e?.message || '')) throw e;
        console.warn('rated orders: lowRated+rated index missing, falling back to client-side range filter');
        const snap = await spScope(firestore().collection(ORDER_COLLECTION).where('lowRated', '==', true)).get();
        docs = snap.docs.filter((d) => { const r = Number(d.data()?.rated) || 0; return r >= from && r <= to; });
      }
      const next = {};
      docs.forEach((d) => { next[d.id] = toRow(d); });
      setHistoryRows(next);
    } catch (e) {
      console.error('rated orders history', e);
      createNotification('error', e?.message || 'Could not load history', 'Rated Orders');
    }
  }, [spScope]);
  useEffect(() => { if (canViewRatedOrders(user)) loadHistory(dateRange); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(() => (openRows ? Object.values({ ...historyRows, ...openRows }) : null), [openRows, historyRows]);

  // ?order=<id> from the support email / desktop alert: open that order
  useEffect(() => {
    if (deepLinkHandled.current || !rows) return;
    const id = new URLSearchParams(location.search).get('order');
    if (!id) return;
    deepLinkHandled.current = true;
    const found = (openRows || {})[id] || historyRows[id];
    if (found) { setSelectedBooking(found); return; }
    firestore().collection(ORDER_COLLECTION).doc(id).get().then((d) => {
      if (d.exists && (!isProvider || d.data()?.sp_id === user?.uid)) { const row = toRow(d); setHistoryRows((h) => ({ ...h, [d.id]: row })); setSelectedBooking(row); }
      else createNotification('warning', 'That order was not found', 'Rated Orders');
    });
  }, [location.search, rows, openRows, historyRows, isProvider, user?.uid]);

  const filtered = useMemo(() => {
    if (!rows) return null;
    const q = search.trim().toLowerCase();
    return rows.filter((r) => (!filters.statuses.length || filters.statuses.includes(r.caseStatus))
      && (!filters.providers.length || filters.providers.includes(r.sp_id))
      && (!filters.services.length || filters.services.includes(r.service_name))
      && (!q || [r.orderNumber, r.customer_name, r.phone, r.customer_number, r.sp_name, r.washer_name, r.service_name, r.reason, COMPLAINT_STATUS[r.caseStatus]?.label]
        .some((v) => String(v || '').toLowerCase().includes(q))));
  }, [rows, filters, search]);

  const summary = useMemo(() => {
    const list = filtered || [];
    const open = list.filter((r) => r.complaintOpen);
    const resolved = list.filter((r) => r.caseStatus === 'resolved');
    const scores = list.map((r) => r.score).filter((n) => n != null);
    const ttr = resolved.map((r) => Number(r.complaint?.resolvedAt) - Number(r.complaint?.detectedAt || r.rated)).filter((n) => Number.isFinite(n) && n >= 0);
    return {
      open: open.length,
      overdue: open.filter((r) => r.caseStatus === 'pending' && complaintAgeHours(r) > slaHours).length,
      resolved: resolved.length,
      avgScore: scores.length ? (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1) : '—',
      medianTtr: hours(median(ttr)),
    };
  }, [filtered, slaHours]);

  // Apply a CaseModal patch to the row we show. The live queue listener refreshes open rows by
  // itself; history rows are a one-shot read, so patch them here (and keep a just-resolved row
  // visible until the next reload).
  const patchRow = (key, patch) => {
    setHistoryRows((h) => {
      const base = h[key] || (openRows && openRows[key]);
      if (!base) return h;
      const merged = { ...base, ...patch, complaint: { ...(base.complaint || {}), ...(patch.complaint || {}) } };
      if (patch.complaint?.status) merged.caseStatus = patch.complaint.status;
      return { ...h, [key]: merged };
    });
    setCaseFor((c) => (c && c.key === key ? { ...c, ...patch, complaint: { ...(c.complaint || {}), ...(patch.complaint || {}) } } : c));
  };

  // Providers don't get the customer's number (same as the table) or any of support's case handling.
  const PROVIDER_HIDDEN_CSV = ['Mobile', 'Case status', 'Assignee', 'Resolved at', 'Resolution', 'Note'];
  const exportCSV = (data) => {
    const allHeaders = ['Rated at', 'Order #', 'Customer', 'Mobile', 'Service', 'Vehicle', 'Provider', 'Worker', 'Rating', 'Service rate', 'Worker rate', 'Arrival rate', 'Comment', 'Case status', 'Assignee', 'Resolved at', 'Resolution', 'Note'];
    const keep = allHeaders.map((h) => !(isProvider && PROVIDER_HIDDEN_CSV.includes(h)));
    const header = allHeaders.filter((_, i) => keep[i]);
    const lines = (data || []).map((r) => [
      r.rated ? moment(r.rated).format('YYYY-MM-DD HH:mm') : '', r.orderNumber, r.customer_name, r.phone, r.service_name, r.vehicle, r.sp_name, r.washer_name,
      r.score, r.serviceRate, r.workerRate, r.arrivalTimingRate, r.reason || '', COMPLAINT_STATUS[r.caseStatus]?.label || 'Pending', r.complaint?.assigneeName || '',
      r.complaint?.resolvedAt ? moment(r.complaint.resolvedAt).format('YYYY-MM-DD HH:mm') : '', r.complaint?.resolutionType || '', r.complaint?.resolutionNote || '',
    ].filter((_, i) => keep[i]).map(csvCell).join(','));
    const link = document.createElement('a');
    link.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent([header.map(csvCell).join(','), ...lines].join('\n'));
    link.download = `rated-orders-${moment(dateRange[0]).format('YYYYMMDD')}-${moment(dateRange[1]).format('YYYYMMDD')}.csv`;
    link.click();
  };

  const isStaff = accessTypes.includes(user?.accessType);
  const isDesktop = deviceWidth() === 'desktop';

  const tableColumns = useMemo(() => [
    {
      Header: 'Rated', accessor: 'rated', width: 118,
      Cell: ({ value }) => <div className="datefieldText">{value ? <>{moment(value).format('DD/MM/YYYY')}<br />{moment(value).format('hh:mm A')}</> : '--'}</div>,
    },
    { Header: 'Order', accessor: 'orderNumber', type: 'label', width: 100, formatValue: (v) => `#${v || '--'}` },
    { Header: 'Customer', accessor: 'customer_name', type: 'customer', showCustomerNumber: isStaff, minWidth: 140 },
    {
      Header: 'Service', accessor: 'service_name', show: isDesktop, minWidth: 150,
      Cell: ({ original }) => (
        <div className="list-item-heading" style={{ whiteSpace: 'normal' }}>
          {original.service_name || '--'}
          {original.vehicle ? <><br /><span className="text-muted text-small">{original.vehicle}</span></> : null}
        </div>
      ),
    },
    {
      Header: 'Rating', accessor: 'score', width: 105,
      Cell: ({ original }) => (
        <div title={`Service ${original.serviceRate ?? '—'} · Worker ${original.workerRate ?? '—'} · Arrival ${original.arrivalTimingRate ?? '—'}`}>
          <Rating total={5} rating={Math.round(original.score || 0)} />
          <span className="text-small font-weight-bold" style={{ color: '#c43d4b' }}>{original.score ?? '—'} / 5</span>
        </div>
      ),
    },
    {
      Header: 'Complaint', accessor: 'reason', minWidth: 230,
      Cell: ({ value }) => <div className={`list-item-heading ${value ? '' : 'text-muted'}`} style={{ whiteSpace: 'normal', lineHeight: 1.35 }}>{value || 'No comment'}</div>,
    },
    {
      Header: 'Service Provider', accessor: 'sp_name', show: isStaff && isDesktop, minWidth: 160,
      Cell: ({ original }) => (
        <div className="list-item-heading" style={{ whiteSpace: 'normal' }}>
          {original.sp_name || '--'}
          {original.washer_name ? <><br /><span className="text-muted text-small">{original.washer_name}</span></> : null}
        </div>
      ),
    },
    {
      Header: 'Case', accessor: 'caseStatus', width: 130, show: !isProvider,
      Cell: ({ original }) => {
        const st = original.caseStatus || 'pending';
        const overdue = st === 'pending' && complaintAgeHours(original) > slaHours;
        return (
          <div>
            <Badge color={COMPLAINT_STATUS[st]?.color || 'secondary'} pill style={PILL} title={COMPLAINT_STATUS[st]?.hint}>{COMPLAINT_STATUS[st]?.label || st}</Badge>
            {overdue ? <div className="text-small" style={{ color: '#e08a00' }}>● over {slaHours} h</div> : null}
            {isStaff && original.complaint?.assigneeName ? <div className="text-muted text-small">{original.complaint.assigneeName}</div> : null}
            {isStaff && original.complaint?.compensation ? <div className="text-small" style={{ color: '#3e884f' }}>AED {Number(original.complaint.compensationTotal || original.complaint.compensation.amount).toFixed(2)} issued</div> : null}
          </div>
        );
      },
    },
  ], [isStaff, isDesktop, isProvider, slaHours]); // eslint-disable-line react-hooks/exhaustive-deps

  // "Manage Case" next to the shared table's View button (120px each, rendered by MainPage).
  const caseActionButtons = useMemo(() => (canManage ? [
    { accessor: 'case', color: 'warning', title: 'Case', func: (r) => setCaseFor(r) },
  ] : []), [canManage]);

  const filterComponent = useMemo(() => {
    // Options come from the loaded complaints only (not every provider on the platform), with
    // "N open" per option, most complaints first. Filtering by status/search does not change them.
    const countOptions = (keyFn, labelFn) => {
      const m = new Map();
      (rows || []).forEach((r) => {
        const k = keyFn(r); if (!k) return;
        const e = m.get(k) || { key: k, value: k, name: labelFn(r) || '—', total: 0, open: 0 };
        e.total += 1; if (r.complaintOpen) e.open += 1; m.set(k, e);
      });
      return [...m.values()].sort((a, b) => b.open - a.open || b.total - a.total || a.name.localeCompare(b.name))
        .map((e) => ({ ...e, label: `${e.name} · ${e.open} open${e.total > e.open ? ` / ${e.total}` : ''}` }));
    };
    const providerOptions = countOptions((r) => r.sp_id || r.sp_name, (r) => r.sp_name || provider?.[r.sp_id]?.name);
    const serviceOptions = countOptions((r) => r.service_name, (r) => r.service_name);
    // Case tiles (open / overdue / resolved / time to resolve) are support's; providers get volume + score.
    const tiles = isProvider ? [
      { title: 'Low ratings', value: (filtered || []).length, color: (filtered || []).length ? '#c43d4b' : '#3e884f', icon: 'simple-icon-bell' },
      { title: 'Average low score', value: summary.avgScore, color: '#c43d4b', icon: 'simple-icon-star', suffix: summary.avgScore !== '—' ? ' / 5' : '' },
    ] : [
      { title: 'Open complaints', value: summary.open, color: summary.open ? '#c43d4b' : '#3e884f', icon: 'simple-icon-bell' },
      { title: `Pending over ${slaHours} h`, value: summary.overdue, color: summary.overdue ? '#e08a00' : '#3e884f', icon: 'simple-icon-clock' },
      { title: 'Resolved in range', value: summary.resolved, color: '#3e884f', icon: 'simple-icon-check' },
      { title: 'Average low score', value: summary.avgScore, color: '#c43d4b', icon: 'simple-icon-star', suffix: summary.avgScore !== '—' ? ' / 5' : '' },
      { title: 'Median time to resolve', value: summary.medianTtr, color: '#145388', icon: 'simple-icon-hourglass' },
    ];
    return (
      <>
        <Colxx xxs="12" md="3" className="mb-3">
          <DatePicker
            onChange={(dates) => { setDateRange(dates); if (dates[1]) loadHistory(dates); }}
            startDate={dateRange[0]} endDate={dateRange[1]} selectsRange={true} dateFormat={'dd/MM/yyyy'} placeholderText="Resolved between"
            popperClassName="filter-datepicker" popperPlacement="bottom-start" showPopperArrow={false}
          />
        </Colxx>
        {!isProvider && <Colxx xxs="12" md="2" className="mb-3">
          <Select isClearable isMulti placeholder="Case status" options={statusOptions} className="basic-multi-select" classNamePrefix="select"
            value={statusOptions.filter((o) => filters.statuses.includes(o.key))}
            onChange={(val) => setFilters((f) => ({ ...f, statuses: (val || []).map((v) => v.key) }))} />
        </Colxx>}
        {isStaff && providerOptions.length > 0 && (
          <Colxx xxs="12" md="2" className="mb-3">
            <Select isClearable isMulti placeholder="Service Providers" options={providerOptions} className="basic-multi-select" classNamePrefix="select" formatOptionLabel={countLabel}
              value={providerOptions.filter((o) => filters.providers.includes(o.key))}
              onChange={(val) => setFilters((f) => ({ ...f, providers: (val || []).map((v) => v.key) }))} />
          </Colxx>
        )}
        <Colxx xxs="12" md="2" className="mb-3">
          <Select isClearable isMulti placeholder="Services" options={serviceOptions} className="basic-multi-select" classNamePrefix="select" formatOptionLabel={countLabel}
            value={serviceOptions.filter((o) => filters.services.includes(o.key))}
            onChange={(val) => setFilters((f) => ({ ...f, services: (val || []).map((v) => v.key) }))} />
        </Colxx>
        <Colxx xxs="12" style={{ borderTop: '1px solid #f0f0f0', paddingTop: '14px' }}>
          {/* Responsive grid: 5 across on wide screens, 2 per row on phones, no divider lines to misalign when wrapping */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px' }}>
            {tiles.map((t) => (
              <div key={t.title} className="d-flex align-items-center" style={{ padding: '10px 12px', borderRadius: '10px', background: '#fafbfc', border: '1px solid #f0f0f0', minWidth: 0 }}>
                <div className="d-flex align-items-center justify-content-center mr-3" style={{ width: '36px', height: '36px', borderRadius: '10px', background: `${t.color}14`, color: t.color, flex: 'none' }}>
                  <i className={t.icon} style={{ fontSize: '15px' }} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div className="salesValue" style={{ color: t.color, lineHeight: 1.1 }}>{t.value}<span className="text-muted" style={{ fontSize: '0.8rem', fontWeight: 400 }}>{t.suffix || ''}</span></div>
                  <div className="salesTitle" style={{ lineHeight: 1.2, fontSize: '0.8rem' }}>{t.title}</div>
                </div>
              </div>
            ))}
          </div>
        </Colxx>
      </>
    );
  }, [provider, rows, filtered, filters, dateRange, summary, slaHours, isStaff, isProvider, loadHistory]);

  if (!canViewRatedOrders(user)) return <Redirect to="/error" />;

  return (
    <div>
      {selectedBooking && (
        <Order
          bookings={bookings} washers={washers} providers={provider} activeServices={services}
          toggleModal={() => setSelectedBooking(false)} dismiss={() => setSelectedBooking(false)}
          selectedBooking={JSON.parse(JSON.stringify(selectedBooking))} editBooking={true} fromHistory={true}
          update={() => setSelectedBooking(false)}
        />
      )}

      <ActiveHelper
        data={filtered}
        title="Rated Orders"
        selected="ratedOrders"
        tableColumns={tableColumns}
        filterComponent={filterComponent}
        exportCSV={exportCSV}
        defaultSorting="rated"
        sortDirection="desc"
        renderAsModal={true}
        hideDefaultDeleteButton={true}
        customEditTitle="View Order"
        customEditFunction={(e) => e && setSelectedBooking(e)}
        customRowClickFunction={(e) => e && setSelectedBooking(e)}
        customActionButtons={caseActionButtons}
      />

      {canManage && <CaseModal booking={caseFor} user={user} isOpen={!!caseFor} dismiss={() => setCaseFor(null)} onUpdated={(patch) => caseFor && patchRow(caseFor.key, patch)} />}
    </div>
  );
};

export default RatedOrders;
